import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useGetPremise,
  useGetPremiseBySearch,
} from "@features/mx/readCycleManagement/hooks";
import { PremiseFiltersComponent } from "./filter";

interface PremiseData {
  id: string;
  premiseName: string;
  area: string;
  subarea: string;
  meters: number;
  consumers: number;
}

interface PremiseFilters {
  area: string;
  subarea: string;
}

export const PremisesTab = ({ readCycleData }: { readCycleData: any }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL including page
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const area = searchParams.get("area") || "";
  const subarea = searchParams.get("subarea") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: getPremise } = useGetPremise(
    readCycleData?.premises,
    currentPage,
    10,
    area,
    subarea,
  );

  const { data: getPremiseBySearch } = useGetPremiseBySearch(
    readCycleData?.premises,
    searchQuery,
    currentPage,
    10,
  );

  // Determine which data to use based on search query
  const isSearchActive = searchQuery.trim().length > 0;
  const activeData = isSearchActive ? getPremiseBySearch : getPremise;

  // Transform API data: extract fields from "_" and convert snake_case keys to camelCase
  const premiseDetail = useMemo(
    () =>
      (activeData?.result || []).map((item: any) => ({
        id: item.hierarchyCodes?.premise,
        name:
          item._?.name || item.hierarchyNames?.premise || item?.name || "NA",
        hierarchyNames: {
          area: item.hierarchyNames?.area || "NA",
          subArea: item.hierarchyNames?.subArea || "NA",
        },
        installedMeterCount: item.installedMeterCount ?? 0,
        totalConsumerCount: item.totalConsumerCount ?? 0,
      })),
    [activeData?.result],
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = activeData?.count;
    const totalPages = Math.ceil(totalItems / 10);
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: 10,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [activeData?.count, currentPage]);

  const handleApplyFilters = (filters: PremiseFilters) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(filters.area &&
        filters.area !== "All Areas" && { area: filters.area }),
      ...(filters.subarea &&
        filters.subarea !== "All Subareas" && {
        subarea: filters.subarea,
      }),
    };

    // Remove params if "All" is selected
    if (filters.area === "All Areas" || !filters.area) {
      delete newParams.area;
    }
    if (filters.subarea === "All Subareas" || !filters.subarea) {
      delete newParams.subarea;
    }

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.area;
    delete newParams.subarea;
    delete newParams.search;
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(area && { area }),
      ...(subarea && { subarea }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Define table columns - use useMemo to prevent re-creation
  const columns = useMemo<TableColumn<PremiseData>[]>(
    () => [
      {
        key: "name",
        header: "Premise",
        sortable: true,
      },
      {
        key: "area",
        header: "Area",
        sortable: true,
        render: (value: string, row: any) => (
          <span>{row.hierarchyNames.area || "N/A"}</span>
        ),
      },
      {
        key: "subarea",
        header: "Subarea",
        sortable: true,
        render: (value: string, row: any) => (
          <span>{row.hierarchyNames.subArea || "N/A"}</span>
        ),
      },
      {
        key: "installedMeterCount",
        header: "Resource",
        sortable: true,
      },
      {
        key: "totalConsumerCount",
        header: "Consumers",
        sortable: true,
      },
    ],
    [],
  );

  // Use the filter component
  const filterPanel = (
    <PremiseFiltersComponent
      area={area}
      subarea={subarea}
      onApplyFilters={handleApplyFilters}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = area || subarea;

  return (
    <div className="space-y-6">
      <div>
        <PageHeader
          title="Premises"
          subtitle="Search, filter, and sort premises associated with this read cycle."
        />
      </div>

      <div className="space-y-4">
        <GenericTable
          data={premiseDetail}
          columns={columns}
          rowKey="id"
          emptyMessage="No premises found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by premise name or address"
          filterPanel={filterPanel}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {[!!area, !!subarea].filter(Boolean).length} filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};
